import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '../db/database'
import { formatFullDate } from '../lib/dates'
import { PendingConfirmations } from './PendingConfirmations'
import { Icon } from './Icon'
import './DayConfirmSheet.css'

/**
 * Ce qu'il reste à confirmer POUR UNE JOURNÉE.
 *
 * Ouverte depuis une notification, et seulement depuis là (2026-09-05). Une
 * notification qui dépose sur la liste des opérations laisse l'utilisateur
 * chercher ce qu'on vient de lui annoncer ; celle-ci l'amène au geste.
 *
 * ⚠️ **Ce n'est pas le détail d'une transaction, et ça ne peut pas l'être.**
 * Une journée prévue non confirmée vit dans `budget_allocations` : la
 * transaction n'existe pas encore, c'est la confirmation qui la crée
 * (invariant 3 — `transactions` ne veut dire qu'une chose, de l'argent a
 * vraiment bougé). L'objet qu'on montre ici est donc le prévisionnel, jamais
 * une opération.
 *
 * Elle se referme d'elle-même quand il n'y a plus rien : `PendingConfirmations`
 * ne rend rien sur une liste vide, et une feuille vide sans son bouton donnerait
 * l'impression d'un écran cassé.
 */
export function DayConfirmSheet(
  { walletId, day, onClose }: { walletId: string; day: string; onClose: () => void },
) {
  // La question posée à la BASE et non au rendu : c'est un fait, pas un instant
  // d'affichage. Elle décide aussi de la fermeture automatique.
  const pending = useLiveQuery(async () => (
    (await db.budget_allocations.where({ wallet_id: walletId }).toArray())
      .filter((a) => !a.deleted_at && (a.confirmed_at ?? null) === null
                     && a.planned_date === day)
      .length
  ), [walletId, day], undefined)

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true"
         aria-label={`Journée du ${formatFullDate(day)}`}>
      <div className="sheet day-sheet">
        <header className="sheet-head">
          <h2>{formatFullDate(day)}</h2>
          <button type="button" className="sheet-close" aria-label="Fermer"
                  onClick={onClose}>
            <Icon name="close" size={18} />
          </button>
        </header>

        {pending === 0 ? (
          <p className="day-sheet-done">
            <Icon name="check" size={18} />
            Tout est confirmé pour cette journée.
          </p>
        ) : (
          <>
            <p className="day-sheet-help">
              Dis ce qui a vraiment été dépensé. Le montant proposé est celui
              qui était prévu — corrige-le s'il ne colle pas.
            </p>
            <PendingConfirmations walletId={walletId} scope={{ day }} bare />
          </>
        )}
      </div>
    </div>
  )
}
